import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiArrowUpRight } from "react-icons/fi";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { PROD_LINK, STAGING_LINK } from "../constant";
import { IS_STAGING } from "../constant/api";

/** The sections worth offering when someone lands on a dead URL. */
const QUICK_LINKS = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "Users", path: "/users" },
  { label: "Orders", path: "/orders" },
  { label: "Payouts", path: "/payouts" },
  { label: "Merchants", path: "/merchants" },
  { label: "Wallet", path: "/wallet" },
  { label: "KYC logs", path: "/kyc-logs" },
  { label: "Settings", path: "/settings" },
];

/**
 * Catch-all route. Signed-out visitors never reach this: auth-context sends
 * any tokenless session to /login on mount, so this always renders inside the
 * authenticated shell.
 */
const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const attemptedPath = `${location.pathname}${location.search}`;

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      <div className="w-full h-[31px] shrink-0 bg-[#1B1B1B] flex justify-center items-center">
        {IS_STAGING ? (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in Staging:{" "}
            <span>
              <a href={PROD_LINK} className="text-[#03C668]">
                Switch to Prod
              </a>
            </span>
          </h4>
        ) : (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in PROD:{" "}
            <span>
              <a href={STAGING_LINK} className="text-[#DF4E4E]">
                Switch to Staging
              </a>
            </span>
          </h4>
        )}
      </div>
      <div className="flex flex-1 min-h-0 w-full flex-col lgss:flex-row overflow-hidden">
        <Sidebar />
        <div className="w-full lgss:w-10/12 flex-1 min-h-0 min-w-0 overflow-y-auto bg-[#13181B]">
          <Navbar />
          <div className="w-full px-5 pt-8 pb-24 mds:px-12 lgss:px-10 font-manrope bg-[#13181B]">
            <div className="rise-in max-w-[640px] mt-6 mds:mt-12">
              <p
                aria-hidden="true"
                className="font-sora text-[88px] mds:text-[112px] font-extrabold leading-none tracking-[-0.04em] text-[#1C2227] select-none"
              >
                404
              </p>

              <h1 className="font-sora text-[26px] mds:text-[32px] font-bold leading-tight text-white mt-4">
                This page doesn't exist
              </h1>
              <p className="text-[15px] leading-relaxed text-[#869AA9] mt-3">
                The link may be out of date, or the address was mistyped.
                Nothing has gone wrong with your session.
              </p>

              <div className="mt-6 rounded-lg border border-[#27313A] bg-[#1C2227] px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#617889]">
                  Address you tried
                </p>
                <p className="mt-1.5 font-mono text-[13px] break-all text-[#C6D0D7]">
                  {attemptedPath}
                </p>
              </div>

              <div className="mt-8 flex flex-col gap-3 xs:flex-row xs:items-center">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="h-[44px] px-5 rounded-lg bg-[#0B84CB] text-white text-[14px] font-semibold transition-colors hover:bg-[#0A76B4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#78C2F7] focus-visible:ring-offset-2 focus-visible:ring-offset-[#13181B]"
                >
                  Go to dashboard
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="h-[44px] px-5 rounded-lg border border-[#27313A] text-[#C6D0D7] text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors hover:border-[#3A4852] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#78C2F7] focus-visible:ring-offset-2 focus-visible:ring-offset-[#13181B]"
                >
                  <FiArrowLeft className="text-[16px]" aria-hidden="true" />
                  Back
                </button>
              </div>

              <div className="mt-10 pt-6 border-t border-[#23282D]">
                <h2 className="text-[13px] font-semibold text-[#869AA9]">
                  Jump to a section
                </h2>
                <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2.5">
                  {QUICK_LINKS.map((link) => (
                    <li key={link.path}>
                      <button
                        type="button"
                        onClick={() => navigate(link.path)}
                        className="group flex items-center gap-1 rounded text-[14px] font-medium text-[#78C2F7] transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#78C2F7] focus-visible:ring-offset-2 focus-visible:ring-offset-[#13181B]"
                      >
                        {link.label}
                        <FiArrowUpRight
                          className="text-[14px] opacity-60 transition-opacity group-hover:opacity-100"
                          aria-hidden="true"
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
